import { computed, reactive } from "vue"
import { setAppBadge } from "../services/badgeService"
import {
  cancelAllNotifications,
  checkNotificationPermissions,
  DEFAULT_NOTIFICATION_PREFERENCES,
  initializeNotifications,
  isNotificationSupported,
  type NotificationPreferences,
  refreshNotifications,
  requestNotificationPermissions,
  scheduleBillReminders,
  sendBudgetAlert,
  sendGoalMilestoneNotification,
} from "../services/notificationService"
import { getUpcomingRecurringExpenses } from "../services/recurringTransactionService"
import type { FirestoreCategory } from "../types/firestore"
import { logger } from "../utils/logger"
import { scheduleSave } from "../utils/storageQuota"

const STORAGE_KEY = "budget-notification-preferences"

interface NotificationState {
  preferences: NotificationPreferences
  permissionGranted: boolean
  isSupported: boolean
  isInitialized: boolean
  lastRefresh: Date | null
}

// Load preferences from localStorage
function loadPreferences(): NotificationPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...DEFAULT_NOTIFICATION_PREFERENCES, ...parsed }
    }
  } catch (error) {
    logger.warn("Failed to load notification preferences", {
      error: error instanceof Error ? error.message : String(error),
    })
  }
  return { ...DEFAULT_NOTIFICATION_PREFERENCES }
}

// Create reactive state
const state = reactive<NotificationState>({
  preferences: loadPreferences(),
  permissionGranted: false,
  isSupported: isNotificationSupported(),
  isInitialized: false,
  lastRefresh: null,
})

// Coalesce preference persistence + refresh: a burst of toggles in the settings
// pane collapses into one localStorage write and one notification refresh per
// idle tick instead of one per nested boolean change.
function persistPreferences(): void {
  scheduleSave(STORAGE_KEY, () => JSON.stringify(state.preferences))
  if (state.isInitialized && state.permissionGranted) {
    // Fire-and-forget by design, but a rejection must not become an unhandled
    // promise rejection — the preference itself is already saved.
    refreshNotifications(state.preferences).catch((error) =>
      logger.warn("Failed to reschedule notifications after preference change", { error }),
    )
  }
}

/**
 * Initialize the notification system
 */
export async function initNotificationStore(): Promise<void> {
  if (state.isInitialized) return

  state.isSupported = isNotificationSupported()

  if (!state.isSupported) {
    state.isInitialized = true
    return
  }

  // Check current permission status
  state.permissionGranted = await checkNotificationPermissions()

  // If notifications are enabled in preferences, initialize
  if (state.preferences.enabled && state.permissionGranted) {
    await initializeNotifications()
    await refreshNotifications(state.preferences)
    state.lastRefresh = new Date()
  }

  state.isInitialized = true
}

/**
 * Request notification permissions and enable notifications
 */
export async function enableNotifications(): Promise<boolean> {
  if (!state.isSupported) return false

  const granted = await requestNotificationPermissions()
  state.permissionGranted = granted

  if (granted) {
    state.preferences.enabled = true
    persistPreferences()
    await initializeNotifications()
    await refreshNotifications(state.preferences)
    state.lastRefresh = new Date()
  }

  return granted
}

/**
 * Disable all notifications
 */
export async function disableNotifications(): Promise<void> {
  state.preferences.enabled = false
  persistPreferences()
  await cancelAllNotifications()
}

/**
 * Update notification preferences
 */
export function updatePreferences(updates: Partial<NotificationPreferences>): void {
  Object.assign(state.preferences, updates)
  persistPreferences()
}

/**
 * Manually refresh all scheduled notifications
 */
export async function refreshAllNotifications(): Promise<number> {
  if (!state.isSupported || !state.permissionGranted || !state.preferences.enabled) {
    return 0
  }

  const count = await scheduleBillReminders(state.preferences)
  state.lastRefresh = new Date()

  // Mirror "bills due within the reminder window" on the app icon badge.
  const dueSoon = getUpcomingRecurringExpenses(state.preferences.billReminderDays).length
  void setAppBadge(dueSoon)

  return count
}

/**
 * Trigger a budget alert check for a category
 */
export async function checkBudgetAlert(category: FirestoreCategory, spent: number): Promise<void> {
  if (!category.budget || category.budget <= 0) return

  await sendBudgetAlert(category, spent, category.budget, state.preferences)
}

/**
 * Trigger a goal milestone notification check
 */
export async function checkGoalMilestone(goalName: string, currentAmount: number, targetAmount: number): Promise<void> {
  await sendGoalMilestoneNotification(goalName, currentAmount, targetAmount, state.preferences)
}

// Computed exports
export const notificationState = {
  preferences: computed(() => state.preferences),
  permissionGranted: computed(() => state.permissionGranted),
  isSupported: computed(() => state.isSupported),
  isInitialized: computed(() => state.isInitialized),
  lastRefresh: computed(() => state.lastRefresh),
}

// Export reactive state for direct access in components
export { state as notificationReactiveState }
